import { Text } from "@solomon-labs/design/components/ui";
import * as React from "react";

import { ToggleGroup, ToggleGroupItem } from "./toggle-group";

export interface SegmentedSelectOption {
    value: string;
    label: string;
    caption?: string;
    disabled?: boolean;
}

export interface SegmentedSelectProps {
    options: SegmentedSelectOption[];
    value: string;
    onValueChange: (value: string) => void;
    disabled?: boolean;
}

/**
 * Single-select segmented control built on the shared ToggleGroup. Renders
 * full-width equal segments, each stacking a body-size label over an optional
 * fine-print muted caption, centered. The mechanism behind captioned segmented
 * pickers (e.g. KYC tier selection) — policy (which options, which are
 * disabled) lives in the consumer.
 */
export function SegmentedSelect({
    options,
    value,
    onValueChange,
    disabled
}: SegmentedSelectProps): React.ReactElement {
    return (
        <ToggleGroup
            variant="outline"
            spacing={2}
            value={[value]}
            onValueChange={(values) => {
                const next = values[0];
                if (typeof next === "string") onValueChange(next);
            }}
            disabled={disabled}
            className="w-full"
        >
            {options.map((option) => (
                <ToggleGroupItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className="flex-1 h-auto flex-col gap-0.5 py-3"
                >
                    <Text variant="body" render={<span />}>
                        {option.label}
                    </Text>
                    {option.caption ? (
                        <Text
                            variant="fine-print"
                            render={<span />}
                            className="opacity-70"
                        >
                            {option.caption}
                        </Text>
                    ) : null}
                </ToggleGroupItem>
            ))}
        </ToggleGroup>
    );
}
